// components/MarketDetail.jsx - 市场详情组件
import React, { useState, useEffect } from 'react';
import './MarketDetail.css';
import './MarketCard.css';
import Toast from './common/Toast';
import { parseUnits, ethers } from 'ethers';
import { AroundUIHelperContract } from '../contracts/AroundUIHelper';
import { AroundPoolFactoryContract } from '../contracts/AroundPoolFactory';
import { LuckyPoolContract } from '../contracts/LuckyPool';
import { AroundMarketContract } from '../contracts/AroundMarket';
import { useEthers } from '../context/EthersContext';
import { getAddresses } from '../utils/address';
import { formatErrorMessage } from '../utils/errors';
import { useLocation, useParams } from 'react-router-dom';
import { formatCurrencyCompact as formatCurrency, formatCompact, formatCurrencyPrecise } from '../utils/numberFormat';
import { loadMarketBasics, parseUserPosition } from '../utils/marketData';

const MarketDetail = ({ onBack, onBetClick, signerOverride }) => {
  const { signer: ctxSigner, provider: ctxProvider, chainId: ctxChainId, addresses: ctxAddrs } = useEthers();
  const location = useLocation();
  const { id: routeId } = useParams();
  const initialMarket = location.state?.market;
  const [market, setMarket] = useState(initialMarket || {
    id: routeId,
    title: '',
    category: 'other',
    volume: 0,
    participants: 0,
    yesPrice: 0,
    noPrice: 0,
    endDate: new Date(),
    liquidity: { virtual: 0, real: 0 },
    resolution: 'pending'
  });
  const [betAmount, setBetAmount] = useState(100);
  const [selectedSide, setSelectedSide] = useState('yes');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [toast, setToast] = useState(null);
  const [collateral, setCollateral] = useState('');
  const [decimals, setDecimals] = useState(6);
  const [addAmount, setAddAmount] = useState('');
  const [removeLpAmount, setRemoveLpAmount] = useState('');
  const [yesPrice, setYesPrice] = useState(market?.yesPrice ?? 0);
  const [noPrice, setNoPrice] = useState(market?.noPrice ?? 0);
  const [buySlippage, setBuySlippage] = useState(null);
  const [sellSlippage, setSellSlippage] = useState(null);
  const [buyOutput, setBuyOutput] = useState(null);
  const [buyFee, setBuyFee] = useState(null);
  const [sellOutput, setSellOutput] = useState(null);
  const [sellFee, setSellFee] = useState(null);
  const [estYesNoShares, setEstYesNoShares] = useState(null);
  const [estRemoval, setEstRemoval] = useState(null);
  const [userYesShares, setUserYesShares] = useState(0);
  const [userNoShares, setUserNoShares] = useState(0);
  const [userLiquidity, setUserLiquidity] = useState(0);
  const [userPotential, setUserPotential] = useState(0);
  const [userLpShares, setUserLpShares] = useState(0);
  const [userLpSharesRaw, setUserLpSharesRaw] = useState(0n);
  const [userRaffleTicketNumber, setUserRaffleTicketNumber] = useState(0);
  const [tradeMode, setTradeMode] = useState('buy');
  const [userCollateralBalance, setUserCollateralBalance] = useState(0);
  const [lpMintEstimate, setLpMintEstimate] = useState(0);
  const [liquidityMode, setLiquidityMode] = useState('add');
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const [showTradeDetails, setShowTradeDetails] = useState(false);
  const [panelMode, setPanelMode] = useState('trade');
  const [titleExpanded, setTitleExpanded] = useState(false);
  const [fees, setFees] = useState(null);
  const [feesLoading, setFeesLoading] = useState(false);
  const [marketLoading, setMarketLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [luckyNumber, setLuckyNumber] = useState(null);
  const [luckyWinner, setLuckyWinner] = useState('');
  const [hasAllot, setHasAllot] = useState(false);
  const maxTitleLen = 56;
  const fullTitle = (market?.title || `Market #${market?.id || routeId || ''}`);
  const displayTitle = (String(fullTitle || '').length > maxTitleLen
    ? `${String(fullTitle).slice(0, maxTitleLen)}…`
    : fullTitle);
  const remainderTitle = String(fullTitle || '').length > maxTitleLen
    ? String(fullTitle).slice(maxTitleLen)
    : '';
  const formatMoney2 = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const mapCategory = (typeId) => {
    const t = Number(typeId);
    switch (t) {
      case 1: return 'crypto';
      case 2: return 'sports';
      case 3: return 'gaming';
      case 4: return 'politics';
      case 5: return 'economy';
      case 6: return 'technology';
      case 7: return 'weather';
      default: return 'other';
    }
  };
  const formatCountdown = (ms) => {
    if (ms <= 0) return 'Ready';
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${h}h ${m}m ${s}s`;
  };

  const ensureNetwork = async (desired) => {
    const net = await ctxProvider.getNetwork();
    if (Number(net?.chainId) === desired) return;
    const hex = '0x' + desired.toString(16);
    try {
      await window.ethereum?.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] });
    } catch {
      const params = (() => {
        if (desired === 421614) {
          return {
            chainId: '0x66eed',
            chainName: 'Arbitrum Sepolia',
            nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
            rpcUrls: [process.env.REACT_APP_ARB_RPC_URL || process.env.REACT_APP_RPC_URL || 'https://arb-sepolia.g.alchemy.com/v2/demo'],
            blockExplorerUrls: ['https://sepolia.arbiscan.io']
          };
        }
        if (desired === 5003) {
          return {
            chainId: '0x138b',
            chainName: 'Mantle Sepolia',
            nativeCurrency: { name: 'MNT', symbol: 'MNT', decimals: 18 },
            rpcUrls: [process.env.REACT_APP_MANTLE_RPC_URL || 'https://rpc.sepolia.mantle.xyz'],
            blockExplorerUrls: ['https://sepolia.mantlescan.xyz']
          };
        }
        return null;
      })();
      if (params) {
        try { await window.ethereum?.request({ method: 'wallet_addEthereumChain', params: [params] }); } catch { }
      }
      try { await window.ethereum?.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] }); } catch { }
    }
    const net2 = await ctxProvider.getNetwork();
    if (Number(net2?.chainId) !== desired) throw new Error('Wrong network');
  };

  const [remainingMs, setRemainingMs] = useState(0);
  useEffect(() => {
    const tick = () => {
      try {
        const end = market?.endDate ? new Date(market.endDate).getTime() : Date.now();
        const left = end - Date.now();
        setRemainingMs(left > 0 ? left : 0);
      } catch {
        setRemainingMs(0);
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [market?.endDate]);
  useEffect(() => {
    const load = async () => {

      try {
        const { AroundUIHelper } = ctxAddrs || {};
        const provider = ctxProvider;
        if (!provider || !AroundUIHelper) return;
        setMarketLoading(true);
        setFeesLoading(true);
        const ui = new AroundUIHelperContract(AroundUIHelper, provider);
        const idBn = ethers.toBigInt(market?.id || routeId || 0);
        const data = await ui.indexMarketData(idBn);
        let col = data?.thisPoolInfo?.collateral || data?.thisMarketInfo?.collateral || '';
        const ZERO = '0x0000000000000000000000000000000000000000';
        if (!col || col === ZERO) {
          const fallback = ctxAddrs?.USDC;
          col = fallback || '';
        }
        setCollateral(col);
        let decLocal = 6;
        try {
          const d = await ui.getDecimals(col);
          console.log('%c🚀[col]-127:', 'color: #a43d3c', col);
          decLocal = Number(d) || 6;
          setDecimals(decLocal);
        } catch (e) { console.error('getDecimals failed', e); }
        console.log('%c🚀[888]-133:', 'color: #860f9e', 888);

        try {
          const yp = await ui.getYesPrice(idBn);
          const np = await ui.getNoPrice(idBn);
          console.log('%c🚀[np]-138:', 'color: #1f4caa', np);
          setYesPrice(parseFloat(Number(ethers.formatUnits(yp, 18)).toFixed(4)));
          setNoPrice(parseFloat(Number(ethers.formatUnits(np, 18)).toFixed(4)));
        } catch (e) { console.error('getYes/NoPrice failed', e); }
        try {
          // Debug: Check data structure
          console.log('🔍 Raw data from indexMarketData:', data);
          console.log('data.thisMarketInfo:', data?.thisMarketInfo);
          console.log('data[2]:', data?.[2]);
          console.log('Is data an array?', Array.isArray(data));
          console.log('Is data.thisMarketInfo an array?', Array.isArray(data?.thisMarketInfo));

          // ethers.js returns named tuples as arrays in some versions
          // Try both object property and array index
          const mi = data?.thisMarketInfo ?? data?.[2];
          const li = data?.thisLiqudityInfo ?? data?.[3];
          console.log('%c🚀[thisLiqudityInfo]-140:', 'color: #0e53d8', li);
          const fi = data?.thisFeeInfo ?? data?.[1];
          console.log('%c🚀[fi]-139:', 'color: #805092', fi);
          try {
            const lpAddr = (data?.thisPoolInfo?.luckyPool ?? data?.thisPoolInfo?.[6] ?? null);
            if (lpAddr) {
              try {
                const lp = new LuckyPoolContract(lpAddr, provider);
                const ln = await lp.getLuckyNumber();
                const lw = await lp.getLuckyWinner();
                setLuckyNumber(Number(ln || 0));
                setLuckyWinner(String(lw || ''));
              } catch (e) { console.error('LuckyPool fetch failed', e); }
            } else {
              setLuckyNumber(null);
              setLuckyWinner('');
            }
          } catch (e) { console.error('LuckyPool mapping failed', e); }
          try {
            const base = (li?.liquidityFeeAmount ?? li?.[3] ?? 0n);
            const denom = ethers.toBigInt(fi?.liquidityFee ?? fi?.[0] ?? 0);
            const calc = (num) => (denom > 0n ? (base * ethers.toBigInt(num || 0)) / denom : 0n);
            const luckyAmt = calc(fi?.luckyFee ?? fi?.[1]);
            const creatorAmt = calc(fi?.creatorFee ?? fi?.[2]);
            const oracleAmt = calc(fi?.oracleFee ?? fi?.[3]);
            const officialAmt = calc(fi?.officialFee ?? fi?.[4]);
            setFees({
              liquidityFee: Number(ethers.formatUnits(base, decLocal)),
              luckyFee: Number(ethers.formatUnits(luckyAmt, decLocal)),
              creatorFee: Number(ethers.formatUnits(creatorAmt, decLocal)),
              oracleFee: Number(ethers.formatUnits(oracleAmt, decLocal)),
              officialFee: Number(ethers.formatUnits(officialAmt, decLocal)),
            });
          } catch (e) { console.error('fees mapping failed', e); setFees(null); }

          // Handle thisMarketInfo array format
          let marketInfo = mi;
          if (Array.isArray(mi)) {
            marketInfo = {
              result: mi[0],
              ifOpenAave: mi[1],
              ifUpdateFinallyAmount: mi[2],
              participants: mi[3],
              startTime: mi[4],
              endTime: mi[5],
              collateral: mi[6],
              totalRaffleTicket: mi[7]
            };
          }

          const mapped = {
            id: (market?.id || routeId || '0'),
            title: (data?.thisPoolInfo?.quest ?? data?.thisPoolInfo?.[4] ?? market?.title ?? ''),
            category: (market?.category || initialMarket?.category || 'other'),
            participants: Number(marketInfo?.participants || 0),
            yesPrice: undefined,
            noPrice: undefined,
            endDate: marketInfo?.endTime ? new Date(Number(marketInfo.endTime) * 1000) : market?.endDate || new Date(),
            liquidity: {
              virtual: Number(ethers.formatUnits((li?.virtualLiquidity ?? li?.[0] ?? 0n), decLocal)),
              real: Number(ethers.formatUnits((li?.tradeCollateralAmount ?? li?.[1] ?? 0n), decLocal)) + Number(ethers.formatUnits((li?.lpCollateralAmount ?? li?.[2] ?? 0n), decLocal)),
              fee: Number(ethers.formatUnits((li?.liquidityFeeAmount ?? li?.[3] ?? 0n), decLocal))
            },
            volume: Number(ethers.formatUnits((li?.volume ?? li?.[7] ?? 0n), decLocal)),
            resolution: marketInfo?.result === 1 ? 'yes' : marketInfo?.result === 2 ? 'no' : 'pending',
            raffleTickets: Number(marketInfo?.totalRaffleTicket || 0)
          };
          setMarket(prev => ({ ...mapped, yesPrice: yesPrice || prev?.yesPrice || 0, noPrice: noPrice || prev?.noPrice || 0 }));
          console.log('%c🚀[setMarket]-270:', 'color: #5a9737', mapped);
          console.log('Updated resolution:', mapped.resolution, 'from result:', marketInfo?.result);
        } catch (e) { console.error('indexMarketData mapping failed', e); }
        finally {
          setMarketLoading(false);
          setFeesLoading(false);
        }
      } catch (e) { console.error('load market initial failed', e); }
    };
    load();
  }, [routeId]);

  const refreshMarket = async () => {
    try {
      const { AroundUIHelper } = ctxAddrs || {};
      const provider = ctxProvider;
      if (!provider || !AroundUIHelper) return;
      setMarketLoading(true);
      setFeesLoading(true);
      const idBn = ethers.toBigInt(market?.id || routeId || 0);
      const basics = await loadMarketBasics(provider, AroundUIHelper, idBn, ctxChainId);
      setYesPrice(basics.yesPrice);
      setNoPrice(basics.noPrice);
      setFees(basics.fees || null);
      console.log('%c🚀[565]-199:', 'color: #253d64', 565);

      setMarket(prev => ({
        ...prev,
        volume: basics.mapped.volume || 0,
        participants: basics.mapped.participants || 0,
        liquidity: basics.mapped.liquidity,
        raffleTickets: basics.mapped.raffleTickets || 0,
        ifOpenAave: Boolean(basics.mapped.ifOpenAave || false)
      }));
    } catch (e) { console.error('refreshMarket failed', e); }
    finally {
      setMarketLoading(false);
      setFeesLoading(false);
    }
  };

  useEffect(() => {
    const initFees = async () => {
      const { AroundUIHelper } = ctxAddrs || {};
      const provider = ctxProvider;
      if (!provider || !AroundUIHelper) return;
      const idBn = ethers.toBigInt(market?.id || routeId || 0);
      setFeesLoading(true);
      try {
        const basics = await loadMarketBasics(provider, AroundUIHelper, idBn, ctxChainId);
        setFees(basics.fees || null);
      } catch {
        setFees(null);
      } finally {
        setFeesLoading(false);
      }
    };
    initFees();
  }, [routeId, ctxProvider]);

  useEffect(() => {
    const fetchAllotState = async () => {
      try {
        const provider = ctxProvider;
        const { AroundPoolFactory } = ctxAddrs || {};
        const idBn = ethers.toBigInt(market?.id || routeId || 0);
        if (!provider || !AroundPoolFactory) return;
        const pf = new AroundPoolFactoryContract(AroundPoolFactory, provider);
        const pi = await pf.getPoolInfo(idBn);
        const poolAddr = (pi?.aroundPool ?? pi?.[1] ?? null);
        if (!poolAddr) return;
        const aroundPool = new ethers.Contract(poolAddr, [
          { inputs: [], name: 'ifAllot', outputs: [{ internalType: 'bool', name: '', type: 'bool' }], stateMutability: 'view', type: 'function' }
        ], provider);
        const flag = await aroundPool.ifAllot();
        setHasAllot(Boolean(flag));
      } catch (e) {
        console.error('fetchAllotState failed', e);
        setHasAllot(false);
      }
    };
    fetchAllotState();
  }, [ctxProvider, ctxAddrs?.AroundPoolFactory, market?.id, routeId]);

  const refreshUserPosition = async () => {
    try {
      const signer = signerOverride || ctxSigner;
      const addr = signer ? await signer.getAddress() : null;
      const { AroundMarket } = ctxAddrs || {};
      const provider = ctxProvider;
      if (!provider || !AroundMarket || !addr) return;
      const am = new AroundMarketContract(AroundMarket, provider);
      const idBn = ethers.toBigInt(market?.id || routeId || 0);
      const pos = await am.getUserMarketPosition(addr, idBn);
      const p = parseUserPosition(pos, decimals);
      setUserYesShares(p.yes);
      setUserNoShares(p.no);
      setUserLiquidity(p.collateral);
      setUserPotential(p.volume);
      setUserLpShares(p.lp);
      setUserLpSharesRaw(p.lpRaw);
      setUserRaffleTicketNumber(p.raffle);
    } catch (e) { console.error('refreshUserPosition failed', e); }
  };

  useEffect(() => {
    const preview = async () => {
      try {
        setPreviewLoading(true);
        const { AroundUIHelper } = ctxAddrs || {};
        const provider = ctxProvider;
        if (!provider || !AroundUIHelper) return;
        const ui = new AroundUIHelperContract(AroundUIHelper, provider);
        const id = ethers.toBigInt(market?.id || routeId || 0);
        const bet = selectedSide === 'yes' ? 1 : 2;
        let decimalsUsed = Number(decimals || 0);
        if (!Number.isFinite(decimalsUsed) || decimalsUsed <= 0) {
          try {
            const col = collateral || (ctxAddrs?.USDC || '');
            const d = await ui.getDecimals(col);
            decimalsUsed = Number(d) || 6;
          } catch {
            decimalsUsed = 6;
          }
        }
        let amount = parseUnits(String(betAmount || 0), decimalsUsed);
        const minAmt = parseUnits('1', decimalsUsed);
        const maxAmt = parseUnits('100000', decimalsUsed);
        if (amount <= 0n) amount = minAmt;
        if (amount > maxAmt) amount = maxAmt;
        let bs = 0n, ss = 0n;
        try { bs = await ui.getBuySlippage(id, bet, amount); } catch (e) { console.error('getBuySlippage failed', e); }
        try { ss = await ui.getSellSlippage(id, bet, amount); } catch (e) { console.error('getSellSlippage failed', e); }
        setBuySlippage(Number(ethers.formatUnits(bs, 18)));
        setSellSlippage(Number(ethers.formatUnits(ss, 18)));
        try {
          const win = await ui.estimateWinAmount(bet, amount, id).catch(() => 0n);
          setBuyOutput({
            output: Number(ethers.formatUnits(win || 0n, Number(decimals || 6))),
          });
          console.log('Preview buy', {
            bet,
            amountWei: amount.toString(),
            marketId: id.toString(),
            decimalsUsed
          });
          try {
            const bo = await ui.getBuyOutput(bet, amount, id);
            const feeOut = (bo?.fee ?? bo?.[1] ?? 0n);
            setBuyFee(Number(ethers.formatUnits(feeOut, decimalsUsed)));
          } catch {
            setBuyFee(null);
          }
        } catch { setBuyOutput(null); setBuyFee(null); }
        try {
          const sellSharesWei = parseUnits(String(tradeMode === 'sell' ? (betAmount || 0) : 0), decimals || 6);
          if (sellSharesWei > 0n) {
            const so = await ui.getSellOutput(bet, sellSharesWei, id);
            setSellOutput(Number(ethers.formatUnits((so?.output ?? so?.[0] ?? 0n), Number(decimals || 6))));
            setSellFee(Number(ethers.formatUnits((so?.fee ?? so?.[1] ?? 0n), Number(decimals || 6))));
            console.log('Preview sell', {
              bet,
              sharesWei: sellSharesWei.toString(),
              marketId: id.toString(),
              decimalsUsed: Number(decimals || 6),
              outputWei: (so?.output ?? so?.[0] ?? 0n).toString(),
              feeWei: (so?.fee ?? so?.[1] ?? 0n).toString()
            });
          } else {
            setSellOutput(null);
            setSellFee(null);
          }
        } catch { setSellOutput(null); setSellFee(null); }

        let addVal = parseUnits(String(addAmount || betAmount || 0), decimalsUsed);
        console.log('%c🚀[addVal]-311:', 'color: #ab627f', addVal);
        if (addVal <= 0n) addVal = parseUnits('1', decimalsUsed);
        try {
          const shares = await ui.getLiquidityShares(id, addVal);
          console.log('%c🚀[shares]-314:', 'color: #d70210', shares);
          setEstYesNoShares({
            yes: Number(ethers.formatUnits((shares?.yesShare ?? shares?.[0] ?? 0), decimalsUsed)),
            no: Number(ethers.formatUnits((shares?.noShare ?? shares?.[1] ?? 0), decimalsUsed))
          });
        } catch (e) { console.error('getLiquidityShares failed', e); }
        try {
          const lpNum = Number(removeLpAmount);
          if (lpNum && lpNum > 0) {
            const lpWei = parseUnits(String(removeLpAmount), decimalsUsed);
            if (lpWei <= userLpSharesRaw) {
              const removal = await ui.getLiquidityRemoval(id, lpWei);
              setEstRemoval({
                collateral: Number(ethers.formatUnits((removal?.collateralAmount ?? removal?.[0] ?? 0), decimals)),
                feeShare: Number(ethers.formatUnits((removal?.feeShare ?? removal?.[1] ?? 0), decimals)),
                totalValue: Number(ethers.formatUnits((removal?.totalValue ?? removal?.[2] ?? 0), decimals))
              });
            } else {
              setEstRemoval(null);
            }
          } else {
            setEstRemoval(null);
          }
        } catch (e) {
          setEstRemoval(null);
        }
      } catch (e) { console.error('getLiquidityRemoval failed', e); }
      finally {
        setPreviewLoading(false);
      }
    };
    preview();
  }, [selectedSide, betAmount, decimals, market?.id, addAmount, removeLpAmount, routeId]);

  useEffect(() => {
    const fetchCollateralBalance = async () => {
      try {
        const signer = signerOverride || ctxSigner;
        const addr = signer ? await signer.getAddress() : null;
        const { AroundUIHelper } = ctxAddrs || {};
        const provider = ctxProvider;
        if (!provider || !AroundUIHelper || !addr || !collateral) return;
        const ui = new AroundUIHelperContract(AroundUIHelper, provider);
        const bal = await ui.getTokenBalance(collateral, addr);
        const num = Number(ethers.formatUnits(bal ?? 0n, decimals || 6));
        setUserCollateralBalance(num);
      } catch (e) { console.error('fetchCollateralBalance failed', e); }
    };
    fetchCollateralBalance();
  }, [collateral, decimals, signerOverride]);

  useEffect(() => {
    const estimateLpMint = async () => {
      try {
        const { AroundUIHelper } = ctxAddrs || {};
        const provider = ctxProvider;
        if (!provider || !AroundUIHelper) return;
        const ui = new AroundUIHelperContract(AroundUIHelper, provider);
        const id = ethers.toBigInt(market?.id || routeId || 0);
        const val = parseUnits(String(addAmount || 0), decimals || 6);
        console.log('%c🚀[setLpMintEstimate]-375:', 'color: #0090a8', val);
        if (val <= 0n) {
          setLpMintEstimate(0);
          return;
        }
        const minted = await ui.getLpSharesToMint(id, val);
        console.log('%c🚀[minted]-379:', 'color: #8b157c', minted);
        setLpMintEstimate(Number(ethers.formatUnits(minted ?? 0n, decimals || 6)));
      } catch (e) {
        console.log('%c🚀[getLpSharesToMint]-382:', 'color: #5032fa', e);
        setLpMintEstimate(0);
      }
    };
    estimateLpMint();
  }, [addAmount, decimals, market?.id, routeId]);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const signer = signerOverride || ctxSigner;
        const addr = signer ? await signer.getAddress() : null;
        const { AroundMarket } = ctxAddrs || {};
        const provider = ctxProvider;
        if (!provider || !AroundMarket || !addr) return;
        const am = new AroundMarketContract(AroundMarket, provider);
        const idBn = ethers.toBigInt(market?.id || routeId || 0);
        const pos = await am.getUserMarketPosition(addr, idBn);
        console.log('%c🚀[pos]-375:', 'color: #c61b3e', pos);
        const p = parseUserPosition(pos, decimals);
        console.log('%c🚀[p]-369:', 'color: #2e4dea', p);
        setUserYesShares(p.yes);
        setUserNoShares(p.no);
        setUserLiquidity(p.collateral);
        setUserPotential(p.volume);
        setUserLpShares(p.lp);
        setUserLpSharesRaw(p.lpRaw);
        setUserRaffleTicketNumber(p.raffle);
      } catch (e) { console.error('loadUser failed', e); }
    };
    loadUser();
  }, [market?.id, routeId, decimals, signerOverride]);

  // 计算预计回报
  const calculateReturn = () => {
    const price = selectedSide === 'yes' ? parseFloat(yesPrice) : parseFloat(noPrice);
    const shares = betAmount / price;
    const profit = shares - betAmount;
    return profit > 0 ? profit : 0;
  };

  // 处理下注
  const handlePlaceBet = async () => {
    try {
      setLoading(true);
      setMessage('');
      const desired = Number(ctxAddrs?.chainId);
      console.log('%c🚀[ctxAddrs]-470:', 'color: #580bc6', ctxAddrs);
      const net = await ctxProvider.getNetwork();
      console.log('%c🚀[net]-472:', 'color: #669f1b', net);
      if (Number(net?.chainId) !== desired) {
        const hex = '0x' + desired.toString(16);
        try { await window.ethereum?.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] }); } catch { }
        const net2 = await ctxProvider.getNetwork();
        if (Number(net2?.chainId) !== desired) throw new Error('Wrong network');
      }
      const signer = signerOverride || ctxSigner;
      if (!signer) throw new Error('Please connect wallet');
      const { AroundMarket, AroundUIHelper } = ctxAddrs || {};
      const contract = new AroundMarketContract(AroundMarket, signer);
      let token = collateral;
      const ZERO = '0x0000000000000000000000000000000000000000';
      if (!token || token === ZERO) {
        const fallback = ctxAddrs?.USDC;
        token = fallback;
      }
      const provider = ctxProvider;
      const ui = new AroundUIHelperContract(AroundUIHelper, provider);
      const d = await ui.getDecimals(token);
      const dec = Number(d) || decimals || 6;
      const approveAmount = parseUnits(String(betAmount), dec);
      let amountRaw = parseUnits(String(betAmount), dec);

      // Check market status - use the market object passed from list
      const nowSec = Math.floor(Date.now() / 1000);
      const marketEndTime = Math.floor(new Date(market.endDate || 0).getTime() / 1000);

      console.log('🔍 Market status check in handlePlaceBet:', {
        marketId: market?.id || routeId,
        resolution: market?.resolution,
        resolutionType: typeof market?.resolution,
        endTime: marketEndTime,
        endDate: market.endDate ? new Date(market.endDate).toISOString() : 'N/A',
        nowSec,
        nowDate: new Date().toISOString(),
        isExpired: marketEndTime <= nowSec,
        isResolvedYes: market?.resolution === 'yes',
        isResolvedNo: market?.resolution === 'no',
        isPending: market?.resolution === 'pending'
      });

      // Check if market is expired
      if (marketEndTime > 0 && marketEndTime <= nowSec) {
        const endedMinsAgo = Math.floor((nowSec - marketEndTime) / 60);
        throw new Error(`Market has ended ${endedMinsAgo} minutes ago. Trading is closed.`);
      }

      // Check if market is already resolved
      if (market?.resolution === 'yes' || market?.resolution === 'no') {
        throw new Error(`Market is already resolved (Result: ${market?.resolution?.toUpperCase()}). Trading is closed.`);
      } else if (market?.resolution && market?.resolution !== 'pending') {
        // If resolution has some other unexpected value, also block
        throw new Error(`Market has invalid status: "${market.resolution}". Trading is closed.`);
      }

      const owner = await signer.getAddress();
      const bal = await ui.getTokenBalance(token, owner);
      if (ethers.toBigInt(bal || 0) < approveAmount) throw new Error('Insufficient balance');
      const erc20 = new ethers.Contract(token, [
        "function approve(address spender,uint256 amount) returns (bool)",
        "function allowance(address owner,address spender) view returns (uint256)"
      ], signer);
      const current = await erc20.allowance(owner, AroundMarket);
      if (ethers.toBigInt(current || 0) < approveAmount) {
        const approveTx = await erc20.approve(AroundMarket, approveAmount);
        await approveTx.wait();
      }
      const bet = selectedSide === 'yes' ? 1 : 2;
      const buyId = ethers.toBigInt(market?.id || routeId || 0);
      console.log('Buy tx params', {
        token,
        market: AroundMarket,
        bet,
        amountWei: amountRaw.toString(),
        decimals: dec,
        marketId: buyId.toString()
      });
      const tx = await contract.buyShares(bet, amountRaw, buyId);
      await tx.wait();
      await refreshMarket();
      await refreshUserPosition();
      setToast({ type: 'success', title: 'Trade Successful', message: 'Buy completed', txHash: tx?.hash || null, chainId: ctxChainId });
    } catch (e) {
      console.error(e);
      setToast({ type: 'error', title: 'Transaction failed', message: formatErrorMessage(e, 'Transaction failed') });
    } finally {
      setLoading(false);
    }
  };

  const handleSell = async () => {
    try {
      setLoading(true);
      setMessage('');
      const desired = Number(ctxChainId);
      if (Number.isFinite(desired)) await ensureNetwork(desired);
      const signer = signerOverride || ctxSigner;
      if (!signer) throw new Error('Please connect wallet');
      const { AroundMarket } = ctxAddrs || {};
      const contract = new AroundMarketContract(AroundMarket, signer);

      // Check market status - use the market object passed from list
      const nowSec = Math.floor(Date.now() / 1000);
      const marketEndTime = Math.floor(new Date(market.endDate || 0).getTime() / 1000);

      console.log('🔍 Market status check in handleSell:', {
        marketId: market?.id || routeId,
        resolution: market?.resolution,
        endTime: marketEndTime,
        isResolvedYes: market?.resolution === 'yes',
        isResolvedNo: market?.resolution === 'no',
        isPending: market?.resolution === 'pending'
      });

      // Check if market is expired
      if (marketEndTime > 0 && marketEndTime <= nowSec) {
        throw new Error('Market has ended. Trading is closed.');
      }

      // Check if market is already resolved
      if (market?.resolution === 'yes' || market?.resolution === 'no') {
        throw new Error(`Market is already resolved (Result: ${market?.resolution?.toUpperCase()}). Trading is closed.`);
      }

      const bet = selectedSide === 'yes' ? 1 : 2;
      const available = selectedSide === 'yes' ? Number(userYesShares || 0) : Number(userNoShares || 0);
      if (Number(betAmount || 0) > available) throw new Error('Exceeds shares');
      const amountRaw = parseUnits(String(betAmount), decimals || 6);
      const owner = await signer.getAddress();
      const amRead = new AroundMarketContract(AroundMarket, ctxProvider);
      const idBn = ethers.toBigInt(market?.id || routeId || 0);
      const pos = await amRead.getUserMarketPosition(owner, idBn);
      const rawAvail = selectedSide === 'yes' ? (pos?.yesBalance ?? pos?.[2] ?? 0n) : (pos?.noBalance ?? pos?.[3] ?? 0n);
      if (amountRaw > ethers.toBigInt(rawAvail || 0)) throw new Error('Exceeds shares');
      console.log('Sell tx params', {
        market: AroundMarket,
        bet,
        sharesWei: amountRaw.toString(),
        decimals: Number(decimals || 6),
        marketId: idBn.toString(),
        uiAvailable: available,
        onchainAvailableWei: rawAvail.toString(),
        selectedSide
      });

      const tx = await contract.sellShares(bet, amountRaw, ethers.toBigInt(market?.id || routeId || 0));
      await tx.wait();
      await refreshMarket();
      await refreshUserPosition();
      setToast({ type: 'success', title: 'Trade Successful', message: 'Sell completed', txHash: tx?.hash || null, chainId: ctxChainId });
    } catch (e) {
      console.error(e);
      setToast({ type: 'error', title: 'Transaction failed', message: formatErrorMessage(e, 'Transaction failed') });
    } finally {
      setLoading(false);
    }
  };

  const handleAddLiquidity = async () => {
    try {
      setLoading(true);
      setMessage('');
      const desired = Number(ctxChainId);
      if (Number.isFinite(desired)) await ensureNetwork(desired);
      const signer = ctxSigner || signerOverride;
      if (!signer) throw new Error('Please connect wallet');
      const { AroundMarket } = ctxAddrs || {};
      const contract = new AroundMarketContract(AroundMarket, signer);

      // Check market status - use the market object passed from list
      const nowSec = Math.floor(Date.now() / 1000);
      const marketEndTime = Math.floor(new Date(market.endDate || 0).getTime() / 1000);

      console.log('🔍 Market status check in handleAddLiquidity:', {
        marketId: market?.id || routeId,
        resolution: market?.resolution,
        endTime: marketEndTime,
        isResolvedYes: market?.resolution === 'yes',
        isResolvedNo: market?.resolution === 'no',
        isPending: market?.resolution === 'pending'
      });

      // Check if market is expired
      if (marketEndTime > 0 && marketEndTime <= nowSec) {
        throw new Error('Market has ended. Cannot add liquidity to a closed market.');
      }

      // Check if market is already resolved
      if (market?.resolution === 'yes' || market?.resolution === 'no') {
        throw new Error(`Market is already resolved (Result: ${market?.resolution?.toUpperCase()}). Cannot add liquidity.`);
      }

      const token = collateral;
      const approveAmt = parseUnits(String(addAmount || betAmount), decimals);
      const amountScaled = parseUnits(String(addAmount || betAmount), decimals);
      const { AroundUIHelper } = ctxAddrs || {};
      const provider = ctxProvider;
      const ui = new AroundUIHelperContract(AroundUIHelper, provider);
      const owner = await signer.getAddress();
      const bal = await ui.getTokenBalance(token, owner);
      if (ethers.toBigInt(bal || 0) < approveAmt) throw new Error('Insufficient balance');
      const erc20 = new ethers.Contract(token, [
        "function approve(address spender,uint256 amount) returns (bool)",
        "function allowance(address owner,address spender) view returns (uint256)"
      ], signer);
      const current = await erc20.allowance(owner, AroundMarket);
      if (ethers.toBigInt(current || 0) < approveAmt) {
        const approveTx = await erc20.approve(AroundMarket, approveAmt);
        await approveTx.wait();
      }
      const addId = ethers.toBigInt(market?.id || routeId || 0);
      console.log('AddLiquidity tx params', {
        token,
        market: AroundMarket,
        amountWei: amountScaled.toString(),
        decimals: Number(decimals || 6),
        marketId: addId.toString()
      });
      const tx = await contract.addMarketLiquidity(addId, amountScaled);
      await tx.wait();
      await refreshMarket();
      await refreshUserPosition();
      setToast({ type: 'success', title: 'Operation Successful', message: 'Liquidity added', txHash: tx?.hash || null, chainId: ctxChainId });
    } catch (e) {
      console.error(e);
      setToast({ type: 'error', title: 'Operation failed', message: formatErrorMessage(e, 'Operation failed') });
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    try {
      setLoading(true);
      setMessage('');
      const desired = Number(ctxChainId);
      if (Number.isFinite(desired)) await ensureNetwork(desired);
      const signer = signerOverride || ctxSigner;
      if (!signer) throw new Error('Please connect wallet');
      const { AroundMarket } = ctxAddrs || {};
      const contract = new AroundMarketContract(AroundMarket, signer);
      const rdId = ethers.toBigInt(market?.id || routeId || 0);
      console.log('Redeem tx params', {
        market: AroundMarket,
        marketId: rdId.toString()
      });
      const tx = await contract.redeemWinnings(rdId);
      setToast({ type: 'info', title: 'Transaction Submitted', message: 'Redeeming winnings', txHash: tx?.hash || null, chainId: ctxChainId });
      const receipt = await tx.wait();
      setToast({ type: 'success', title: 'Congratulations!', message: 'Winnings redeemed', txHash: receipt?.transactionHash || tx?.hash, chainId: ctxChainId });
      await refreshMarket();
      await refreshUserPosition();
    } catch (e) {
      console.error(e);
      setToast({ type: 'error', title: 'Operation failed', message: formatErrorMessage(e, 'Operation failed') });
    } finally {
      setLoading(false);
    }
  };

  const handleTouchAllot = async () => {
    try {
      setLoading(true);
      setMessage('');
      const desired = Number(ctxChainId);
      if (Number.isFinite(desired)) await ensureNetwork(desired);
      const signer = signerOverride || ctxSigner;
      if (!signer) throw new Error('Please connect wallet');
      const { AroundMarket } = ctxAddrs || {};
      const am = new AroundMarketContract(AroundMarket, signer);
      const allotId = ethers.toBigInt(market?.id || routeId || 0);
      console.log('TouchAllot tx params', {
        market: AroundMarket,
        marketId: allotId.toString()
      });
      const tx = await am.touchAllot(allotId);
      setToast({ type: 'info', title: 'Transaction Submitted', message: 'Triggering distribution', txHash: tx?.hash || null, chainId: ctxChainId });
      const receipt = await tx.wait();
      setToast({ type: 'success', title: 'Distribution triggered', message: 'Allot executed', txHash: receipt?.transactionHash || tx?.hash, chainId: ctxChainId });
      await refreshMarket();
      await refreshUserPosition();
      setHasAllot(true);
    } catch (e) {
      console.error(e);
      setToast({ type: 'error', title: 'Operation failed', message: formatErrorMessage(e, 'Operation failed') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="market-detail">
      <div className="detail-header">
        <div className="detail-header-grid">
          <div className="header-left">
            <div className="header-row">
              <button className="back-btn" onClick={onBack}>← Back</button>
              <div className="title-wrap" onClick={() => setTitleExpanded(v => !v)}>
                <h1 className={titleExpanded ? 'two-line' : ''} title={fullTitle}>
                  <span className="title-line1">{displayTitle}</span>
                  {titleExpanded && !!remainderTitle && (
                    <span className="title-line2">{remainderTitle}</span>
                  )}
                </h1>
                <span className="market-id-badge">#{market?.id || routeId || ''}</span>
                <span className={`category-tag ${market.category}`}>{market.category}</span>
              </div>
            </div>
          </div>
          <div className="header-right">
            <div className="countdown">
              Ends. {formatCountdown(remainingMs)}
            </div>
          </div>


        </div>
      </div>

      <div className="detail-content">
        <div className="detail-left">

          <div className="top-info">
            <div className="info-section">
              <h3>Market</h3>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-label">Status</div>
                  <div className="info-value">
                    <span className={`status-tag ${market.resolution === 'pending' ? 'active' : 'resolved'}`}>
                      {market.resolution === 'pending' ? 'Active' : 'Resolved'}
                    </span>
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">Ends</div>
                  <div className="info-value">
                    {(market?.endDate ? new Date(market.endDate) : new Date()).toLocaleDateString('en-US', {
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-label">Traders</div>
                  <div className="info-value">{market.participants.toLocaleString()}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Volume</div>
                  <div className="info-value">{formatCurrency(market.volume)}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Raffle Tickets</div>
                  <div className="info-value">{Number(market.raffleTickets || 0).toLocaleString()}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Lucky Number</div>
                  <div className="info-value">{luckyNumber !== null ? Number(luckyNumber).toLocaleString() : '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">Lucky Winner</div>
                  <div className="info-value">{luckyWinner ? `${luckyWinner.slice(0, 6)}...${luckyWinner.slice(-6)}` : '-'}</div>
                </div>
              </div>
            </div>
            <div className="user-stats-section">
              <h3>User Position</h3>
              <div className="user-stats">
                <div className="user-stat">
                  <div className="user-stat-label">Collateral Locked</div>
                  <div className="user-stat-value">{marketLoading ? '-' : formatMoney2(userLiquidity)}</div>
                </div>
                <div className="user-stat">
                  <div className="user-stat-label">LP Shares</div>
                  <div className="user-stat-value">{marketLoading ? '-' : Number(userLpShares).toFixed(2)}</div>
                </div>
                <div className="user-stat">
                  <div className="user-stat-label">YES shares</div>
                  <div className="user-stat-value">{marketLoading ? '-' : Number(userYesShares).toFixed(2)}</div>
                </div>
                <div className="user-stat">
                  <div className="user-stat-label">NO shares</div>
                  <div className="user-stat-value">{marketLoading ? '-' : Number(userNoShares).toFixed(2)}</div>
                </div>
                <div className="user-stat">
                  <div className="user-stat-label">Trading Volume</div>
                  <div className="user-stat-value">{marketLoading ? '-' : formatMoney2(userPotential)}</div>
                </div>
                <div className="user-stat">
                  <div className="user-stat-label">Raffle Ticket</div>
                  <div className="user-stat-value">{marketLoading ? '-' : Number(userRaffleTicketNumber || 0).toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>



          <div className="liquidity-section">
            <h3>Liquidity</h3>
            <div className="liquidity-progress">
              <div
                className="liq-virtual"
                style={{ width: `${(Number(market.liquidity.virtual || 0) + Number(market.liquidity.real || 0)) > 0 ? (Number(market.liquidity.virtual || 0) / (Number(market.liquidity.virtual || 0) + Number(market.liquidity.real || 0)) * 100).toFixed(2) : 0}%` }}
              />
              <div
                className="liq-real"
                style={{ width: `${(Number(market.liquidity.virtual || 0) + Number(market.liquidity.real || 0)) > 0 ? (Number(market.liquidity.real || 0) / (Number(market.liquidity.virtual || 0) + Number(market.liquidity.real || 0)) * 100).toFixed(2) : 0}%` }}
              />
            </div>
            <div className="liquidity-legend">
              <span>Virtual {((Number(market.liquidity.virtual || 0) + Number(market.liquidity.real || 0)) > 0 ? (Number(market.liquidity.virtual || 0) / (Number(market.liquidity.virtual || 0) + Number(market.liquidity.real || 0)) * 100).toFixed(0) : '0')}%</span>
              <span>Real {((Number(market.liquidity.virtual || 0) + Number(market.liquidity.real || 0)) > 0 ? (Number(market.liquidity.real || 0) / (Number(market.liquidity.virtual || 0) + Number(market.liquidity.real || 0)) * 100).toFixed(0) : '0')}%</span>
            </div>
            <div className="liquidity-cards">
              <div className="liquidity-card">
                <div className="liquidity-label">Virtual</div>
                <div className="liquidity-value">{marketLoading ? '-' : formatCurrency(market.liquidity.virtual)}</div>
              </div>
              <div className="liquidity-card">
                <div className="liquidity-label">Real</div>
                <div className="liquidity-value">{marketLoading ? '-' : formatCurrency(market.liquidity.real)}</div>
              </div>
              <div className="liquidity-card">
                <div className="liquidity-label">Total</div>
                <div className="liquidity-value">{marketLoading ? '-' : formatCurrency(market.liquidity.virtual + market.liquidity.real)}</div>
              </div>
              <div className="liquidity-card">
                <div className="liquidity-label">Liquidity Fees</div>
                <div className="liquidity-value">{marketLoading ? '-' : formatCurrencyPrecise(market.liquidity.fee || 0, { min: 2, max: 2 })}</div>
              </div>
            </div>
          </div>

          {feesLoading && (
            <div className="liquidity-section">
              <h3>Fees</h3>
              <div className="liquidity-cards">
                <div className="liquidity-card">
                  <div className="liquidity-label">Loading...</div>
                  <div className="liquidity-value">-</div>
                </div>
              </div>
            </div>
          )}
          {!feesLoading && fees && (
            <div className="liquidity-section">
              <h3>Fees</h3>
              <div className="liquidity-cards">
                <div className="liquidity-card">
                  <div className="liquidity-label">Creator Fee</div>
                  <div className="liquidity-value">{formatCurrencyPrecise(fees.creatorFee || 0, { min: 2, max: 2 })}</div>
                </div>
                <div className="liquidity-card">
                  <div className="liquidity-label">Oracle Fee</div>
                  <div className="liquidity-value">{formatCurrencyPrecise(fees.oracleFee || 0, { min: 2, max: 2 })}</div>
                </div>
                <div className="liquidity-card">
                  <div className="liquidity-label">Lucky Pool Fee</div>
                  <div className="liquidity-value">{formatCurrencyPrecise(fees.luckyFee || 0, { min: 2, max: 2 })}</div>
                </div>
                <div className="liquidity-card">
                  <div className="liquidity-label">Official Fee</div>
                  <div className="liquidity-value">{formatCurrencyPrecise(fees.officialFee || 0, { min: 2, max: 2 })}</div>
                </div>
              </div>
            </div>
          )}

        </div>

        <div className="detail-right">
          <div className="trading-panel sticky-panel">
            <h3>Settlement</h3>
            {(() => {
              return (
                <div className="panel-content">
                  <div className="helper-row">
                    {hasAllot ? 'Ready to redeem' : 'Ready to allot'}
                  </div>
                  <div className="action-buttons">
                    {hasAllot ? (
                      <button
                        className="place-bet-btn"
                        onClick={handleRedeem}
                        disabled={loading}
                        aria-busy={loading}
                      >
                        {loading ? 'Processing…' : 'Redeem Winnings'}
                      </button>
                    ) : (
                      <button
                        className="place-bet-btn"
                        onClick={handleTouchAllot}
                        disabled={loading}
                        aria-busy={loading}
                      >
                        {loading ? 'Processing…' : 'Trigger Distribution'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="trading-panel sticky-panel">
            <h3>Trade</h3>
            <div className="segmented full panel-tabs" role="tablist" aria-label="Primary tabs">
              <div
                className={`segmented-option ${panelMode === 'trade' ? 'active' : ''}`}
                role="tab"
                onClick={() => setPanelMode('trade')}
              >
                Trade
              </div>
              <div
                className={`segmented-option ${panelMode === 'liquidity' ? 'active' : ''}`}
                role="tab"
                onClick={() => setPanelMode('liquidity')}
              >
                Liquidity
              </div>
            </div>

            {panelMode === 'trade' && (
              <div className="panel-content">
                <div className="segmented sub" role="tablist" aria-label="Trade mode">
                  <div
                    className={`segmented-option ${tradeMode === 'buy' ? 'active' : ''}`}
                    role="tab"
                    onClick={() => setTradeMode('buy')}
                  >
                    Buy
                  </div>
                  <div
                    className={`segmented-option ${tradeMode === 'sell' ? 'active' : ''}`}
                    role="tab"
                    onClick={() => setTradeMode('sell')}
                  >
                    Sell
                  </div>
                </div>
                <div className="price-display-large">
                  <div
                    className={`price-option yes ${selectedSide === 'yes' ? 'selected' : ''}`}
                    onClick={() => setSelectedSide('yes')}
                  >
                    <div className="price-label">YES</div>
                    <div className="price-value">{(Number(yesPrice) * 100).toFixed(2)}¢</div>
                  </div>
                  <div
                    className={`price-option no ${selectedSide === 'no' ? 'selected' : ''}`}
                    onClick={() => setSelectedSide('no')}
                  >
                    <div className="price-label">NO</div>
                    <div className="price-value">{(Number(noPrice) * 100).toFixed(2)}¢</div>
                  </div>
                </div>

                <div className="bet-input-section">
                  <div className="input-group">
                    <div className="amount-header">
                      <label htmlFor="bet-amount">{tradeMode === 'buy' ? 'Amount' : 'Shares'}</label>
                      <div className="amount-value">
                        {tradeMode === 'buy'
                          ? formatCurrency(betAmount || 0)
                          : `${(betAmount || 0).toFixed(2)} shares`}
                      </div>
                    </div>
                    <input
                      type="number"
                      id="bet-amount"
                      value={betAmount}
                      onChange={(e) => setBetAmount(parseFloat(e.target.value) || 0)}
                      min="0"
                      step={tradeMode === 'buy' ? '0.01' : '0.0001'}
                    />
                  </div>
                  <div className="helper-row">
                    {tradeMode === 'buy'
                      ? <>Balance: {formatCurrency(userCollateralBalance || 0)}</>
                      : <>Balance: {(selectedSide === 'yes' ? Number(userYesShares) : Number(userNoShares)).toFixed(4)} shares</>}
                  </div>

                  {tradeMode === 'buy' ? (
                    <div className="amount-presets">
                      <button className="amount-preset" onClick={() => setBetAmount(1)}>$1</button>
                      <button className="amount-preset" onClick={() => setBetAmount(20)}>$20</button>
                      <button className="amount-preset" onClick={() => setBetAmount(100)}>$100</button>
                      <button className="amount-preset" onClick={() => setBetAmount(Number(userCollateralBalance || 0))}>Max</button>
                    </div>
                  ) : (
                    <div className="amount-presets">
                      <button className="amount-preset" onClick={() => setBetAmount(Number(((selectedSide === 'yes' ? userYesShares : userNoShares) * 0.25).toFixed(4)))}>25%</button>
                      <button className="amount-preset" onClick={() => setBetAmount(Number(((selectedSide === 'yes' ? userYesShares : userNoShares) * 0.5).toFixed(4)))}>50%</button>
                      <button className="amount-preset" onClick={() => setBetAmount(Number(((selectedSide === 'yes' ? userYesShares : userNoShares) * 0.75).toFixed(4)))}>75%</button>
                      <button className="amount-preset" onClick={() => setBetAmount(Number((selectedSide === 'yes' ? userYesShares : userNoShares).toFixed(4)))}>Max</button>
                    </div>
                  )}

                  <div className="return-calculation">
                    {tradeMode === 'buy' ? (
                      <>
                        <div className="return-row total">
                          <span>Expected Winnings</span>
                          <span>{previewLoading ? 'Calculating…' : (buyOutput !== null ? formatCurrency(Number(buyOutput?.output || 0)) : '-')}</span>
                        </div>
                        {showTradeDetails && (
                          <>
                            <div className="return-row">
                              <span>Shares to receive</span>
                              <span>{previewLoading ? '…' : (betAmount / (selectedSide === 'yes' ? (yesPrice || 1) : (noPrice || 1))).toFixed(2)}</span>
                            </div>
                            <div className="return-row">
                              <span>Fees</span>
                              <span>{buyFee !== null ? formatCurrency(buyFee) : '-'}</span>
                            </div>
                            <div className="return-row">
                              <span>Slippage</span>
                              <span>{buySlippage !== null ? `${(buySlippage * 100).toFixed(2)}%` : '-'}</span>
                            </div>
                          </>
                        )}
                        <button className="details-toggle" onClick={() => setShowTradeDetails(v => !v)}>
                          {showTradeDetails ? 'Hide details' : 'Show details'}
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="return-row total">
                          <span>Net Proceeds</span>
                          <span>{sellOutput !== null ? formatCurrency(sellOutput) : '-'}</span>
                        </div>
                        {showTradeDetails && (
                          <>
                            <div className="return-row">
                              <span>Proceeds</span>
                              <span>{sellOutput !== null ? formatCurrency(sellOutput) : '-'}</span>
                            </div>
                            <div className="return-row">
                              <span>Fees</span>
                              <span>{sellFee !== null ? formatCurrency(sellFee) : '-'}</span>
                            </div>
                            <div className="return-row">
                              <span>Slippage</span>
                              <span>{sellSlippage !== null ? `${(sellSlippage * 100).toFixed(2)}%` : '-'}</span>
                            </div>
                          </>
                        )}
                        <button className="details-toggle" onClick={() => setShowTradeDetails(v => !v)}>
                          {showTradeDetails ? 'Hide details' : 'Show details'}
                        </button>
                      </>
                    )}
                  </div>

                  {tradeMode === 'buy' ? (
                    <button className="place-bet-btn" onClick={handlePlaceBet} disabled={loading} aria-busy={loading}>
                      {loading ? 'Processing…' : `Buy ${selectedSide.toUpperCase()}`}
                    </button>
                  ) : (
                    <button className="place-bet-btn" onClick={handleSell} disabled={loading} aria-busy={loading}>
                      {loading ? 'Processing…' : `Sell ${selectedSide.toUpperCase()}`}
                    </button>
                  )}

                  <Toast
                    open={!!toast}
                    type={toast?.type}
                    title={toast?.title}
                    message={toast?.message}
                    txHash={toast?.txHash}
                    chainId={toast?.chainId}
                    onClose={() => setToast(null)}
                  />
                </div>
              </div>
            )}
            {panelMode === 'liquidity' && (
              <div className="panel-content liquidity-op-card">
                <div className="liquidity-2col">
                  <div>
                    <div className="input-group">
                      <label htmlFor="add-amount">Collateral Amount</label>
                      <div className="input-with-suffix">
                        <input
                          type="number"
                          id="add-amount"
                          value={addAmount}
                          onChange={(e) => setAddAmount(e.target.value)}
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                        />
                        <span className="input-suffix">USDC</span>
                      </div>
                    </div>
                    <div className="helper-row">
                      <span>Balance: {formatCurrency(userCollateralBalance || 0)}</span>
                    </div>
                    <div className="amount-presets">
                      <button className="amount-preset" onClick={() => setAddAmount(String(((userCollateralBalance || 0) * 0.25).toFixed(2)))}>25%</button>
                      <button className="amount-preset" onClick={() => setAddAmount(String(((userCollateralBalance || 0) * 0.5).toFixed(2)))}>50%</button>
                      <button className="amount-preset" onClick={() => setAddAmount(String(((userCollateralBalance || 0) * 0.75).toFixed(2)))}>75%</button>
                      <button className="amount-preset" onClick={() => setAddAmount(String((userCollateralBalance || 0).toFixed(2)))}>Max</button>
                    </div>
                  </div>
                  <div className="liquidity-summary">
                    <div className="summary-title">Summary</div>
                    {estYesNoShares && (
                      <>
                        <div className="return-row">
                          <span>Est. YES shares</span>
                          <span>{estYesNoShares.yes.toFixed(4)}</span>
                        </div>
                        <div className="return-row">
                          <span>Est. NO shares</span>
                          <span>{estYesNoShares.no.toFixed(4)}</span>
                        </div>
                      </>
                    )}
                    <div className="return-row">
                      <span>LP shares to mint</span>
                      <span>{lpMintEstimate.toFixed(6)}</span>
                    </div>
                    <div className="return-row">
                      <span>Available LP</span>
                      <span>{userLpShares.toFixed(6)}</span>
                    </div>
                  </div>
                </div>
                <div className="action-buttons">
                  <button
                    className="place-bet-btn"
                    onClick={handleAddLiquidity}
                    disabled={loading || Number(addAmount || 0) <= 0 || Number(addAmount || 0) > (userCollateralBalance || 0)}
                    aria-busy={loading}
                  >
                    {loading ? 'Processing…' : 'Add Liquidity'}
                  </button>
                </div>
              </div>
            )}
          </div>




        </div>
      </div>

    </div >
  );
};

export default MarketDetail;
